import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock } from 'lucide-react';
import type { UpcomingScheduleItem } from '@/types';
import { useAuth } from '@/store/auth-context';
import { routes } from '@/lib/routes';
import { OverviewPanel } from './OverviewPanel';

interface UpcomingSchedulesPanelProps {
  items?: UpcomingScheduleItem[];
  isLoading?: boolean;
}

function formatLocalTime(iso: string): string {
  return new Date(iso)
    .toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true })
    .toLowerCase()
    .replace(/\s+/g, '');
}

function formatRelativeFuture(iso: string): string {
  const target = new Date(iso).getTime();
  const diffMs = target - Date.now();
  if (diffMs <= 0) return 'now';
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 60) return `in ${Math.max(1, minutes)}m`;
  const hours = Math.floor(diffMs / 3_600_000);
  if (hours < 24) return `in ${hours}h`;
  const days = Math.floor(diffMs / 86_400_000);
  if (days < 7) return `in ${days}d`;
  return new Date(iso).toLocaleDateString();
}

function startOfLocalDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function ordinalSuffix(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return 'th';
  switch (n % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

function formatDayDate(date: Date): string {
  const weekday = date.toLocaleDateString(undefined, { weekday: 'short' });
  const month = date.toLocaleDateString(undefined, { month: 'short' });
  const day = date.getDate();
  return `${weekday} ${month} ${day}${ordinalSuffix(day)}`;
}

function dayHeaderParts(iso: string): { relative?: string; date: string } {
  const date = new Date(iso);
  const today = startOfLocalDay(new Date());
  const day = startOfLocalDay(date);
  const diffDays = Math.round((day - today) / 86_400_000);
  const dateLabel = formatDayDate(date);
  if (diffDays === 0) return { relative: 'Today', date: dateLabel };
  if (diffDays === 1) return { relative: 'Tomorrow', date: dateLabel };
  return { date: dateLabel };
}

interface DayGroup {
  key: number;
  relative?: string;
  date: string;
  items: UpcomingScheduleItem[];
}

function groupByDay(items: UpcomingScheduleItem[]): DayGroup[] {
  const sorted = [...items].sort((a, b) =>
    a.nextFireAt.localeCompare(b.nextFireAt),
  );
  const groups = new Map<number, DayGroup>();
  for (const item of sorted) {
    const key = startOfLocalDay(new Date(item.nextFireAt));
    let group = groups.get(key);
    if (!group) {
      const parts = dayHeaderParts(item.nextFireAt);
      group = { key, relative: parts.relative, date: parts.date, items: [] };
      groups.set(key, group);
    }
    group.items.push(item);
  }
  return [...groups.values()].sort((a, b) => a.key - b.key);
}

export function UpcomingSchedulesPanel({ items, isLoading }: UpcomingSchedulesPanelProps) {
  const list = items ?? [];
  const groups = groupByDay(list);
  const { user } = useAuth();
  const fallbackSpaceId = user?.preferences?.lastSpaceId ?? user?.defaultSpaceId ?? '';
  const schedulesHref = routes.console.spaceAgentSchedules(fallbackSpaceId);

  return (
    <OverviewPanel
      title="Upcoming Schedules"
      icon={<CalendarClock className="h-5 w-5" />}
      action={{ label: 'View all', to: schedulesHref }}
      isLoading={isLoading && !items}
      isEmpty={!isLoading && list.length === 0}
      emptyMessage="Nothing scheduled"
      bodyClassName="p-0"
    >
      <ul className="divide-y divide-border">
        {groups.map((group) => (
          <Fragment key={group.key}>
            <li className="px-4 pt-3 pb-1 text-xs">
              {group.relative && (
                <span className="font-semibold text-foreground">{group.relative}</span>
              )}
              {group.relative && <span className="text-muted-foreground"> · </span>}
              <span className="text-muted-foreground">{group.date}</span>
            </li>
            {group.items.map((schedule) => {
              const taskLabel =
                schedule.name || schedule.scheduleExpression || 'Schedule';
              return (
                <li
                  key={schedule.scheduleId}
                  className="hover:bg-accent/40 transition-colors"
                >
                  <Link
                    to={schedulesHref}
                    className="flex items-start gap-3 px-4 py-2.5"
                  >
                    <span className="w-14 shrink-0 pt-0.5 text-xs font-medium text-foreground tabular-nums whitespace-nowrap text-right">
                      {formatLocalTime(schedule.nextFireAt)}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground break-words">{taskLabel}</p>
                      {schedule.agentName && (
                        <p className="text-xs text-muted-foreground truncate">
                          {schedule.agentName}
                        </p>
                      )}
                    </div>
                    <span className="w-14 shrink-0 pt-0.5 text-xs text-muted-foreground tabular-nums whitespace-nowrap text-right">
                      {formatRelativeFuture(schedule.nextFireAt)}
                    </span>
                  </Link>
                </li>
              );
            })}
          </Fragment>
        ))}
      </ul>
    </OverviewPanel>
  );
}
